import React, { FunctionComponent } from 'react';
import * as s from './button.module.scss';
import Img from '../img';
import { Link } from 'components/shared/link-proxy';
import { IButton } from 'bit/shared/declarations/index';

export interface IButtonStyles {
  [key: string]: any;
  primary?: string | null;
  secondary?: string | null;
  tertiary?: string | null;
  outline?: string | null;
  justText?: string | null;
  icon?: string | null;
  iconHover?: string | null;
  iconButton?: string | null;
  iconWrapper?: string | null;
  hasPrimaryProp?: string | null;
  disabled?: string | null;
  button?: string | null;
  ldsRing?: string | null;
  small?: boolean | null;
  native?: boolean | null;
  transparent?: boolean | null;
  white?: boolean | null;
}

export interface IButtonProps extends IButton {
  children?: React.ReactNode;
  primary?: boolean;
  secondary?: boolean;
  tertiary?: boolean;
  outline?: boolean;
  className?: string;
  type?: 'button' | 'submit';
  [key: string]: unknown;
  disabled?: boolean | null;
  loading?: boolean | null;
  styles?: IButtonStyles | null;
  load?: 'eager' | 'lazy';
  updateGlobal?: (args: any) => void;
}

function assignClass(s: any, className: string, styles?: IButtonStyles | null, condition?: boolean | null) {
  return condition ? styles?.[className] || s[className] : '';
}

const Button: FunctionComponent<IButtonProps> = ({
  id,
  white,
  transparent,
  native,
  small,
  children,
  link,
  primary, // this creates the ilussion that a dev can use this prop but is the default behavior of the component to render a primary color
  secondary,
  tertiary,
  outline,
  className,
  type,
  text,
  justText,
  iconButton,
  loading = false,
  disabled,
  icon,
  iconHover,
  styles,
  load,
  keepQuery,
  updateGlobal,
  ...props
}) => {
  // classes

  const consultationButtonsIds = [
    '144591933',
    '113329867',
    '121695745',
    '84537459',
    'dq3uAX3xQ6OdG0S9V6PAIw',
    'DMXP6f-3Q2ufkLZAyj2TLQ',
    '101181889',
    '78930813',
    '119944621',
    '120870119',
    '121261694',
    '144553873',
    '78929646',
    '78929970',
    '78930822',
    '78930814',
    '78929645',
    '89911160',
    '108445481',
    '111953869',
    '78930818',
    'OnFuqaZQQuOclQzzq0bw9w',
    '78931194',
    '78930886',
    'Xn_h5VnjRvqZcZytlNbrZw',
    '78930856',
    '78931192',
    '78930815',
    '148163449',
    'OudaL77sTu2z5gHLV6wd-w',
    '152123965',
    '85898828',
    '113329833',
    '113329810',
    '121163358',
    'Y5K_BNazTZeM3iKOjFh_lA',
  ];

  const consultationLinksIds = [
    '78935143',
    '78935551',
    'MyaXcMCrTVu--qjQu7650Q',
    '78937174',
    'AE4NncO7S6yp9vH5QjZBHA',
    '121695724',
    '78937187',
    'F8tXAubLS36B4v4IxjVLFg',
    '113329816',
    '152123963',
    '78935858',
    '78935154',
  ];

  const isConsultationButton = consultationButtonsIds.some((el) => {
    return id?.includes(el);
  });
  const isConsultationLink = consultationLinksIds.some((el) => {
    return link?.id?.includes(el);
  });

  if ((isConsultationButton || isConsultationLink) && process.env.GATSBY_LOCALE === 'en-UK') {
    return null;
  }

  const primaryClass = assignClass(s, 'primary', styles, true);
  const secondaryClass = assignClass(s, 'secondary', styles, secondary);
  const tertiaryClass = assignClass(s, 'tertiary', styles, tertiary);
  const outlineClass = assignClass(s, 'outline', styles, outline);
  const iconWrapperClass = assignClass(s, 'iconWrapper', styles, Boolean(icon));
  const iconClass = assignClass(s, 'icon', styles, Boolean(icon));
  const iconHoverClass = assignClass(s, 'iconHover', styles, Boolean(icon));
  const justTextClass = assignClass(s, 'justText', styles, justText);
  const iconButtonClass = assignClass(s, 'iconButton', styles, iconButton);
  const hasPrimaryPropClass = assignClass(s, 'hasPrimaryProp', styles, primary);
  const disabledClass = assignClass(s, 'disabled', styles, disabled);
  const colorClasses = `${secondaryClass || tertiaryClass || primaryClass} ${outlineClass}`;

  const classes = `${assignClass(
    s,
    'button',
    styles,
    true,
  )} ${colorClasses} ${className} ${justTextClass} ${iconButtonClass} ${hasPrimaryPropClass} ${disabledClass}`;

  interface IfinalText {
    iconButton?: boolean | null;
    justText?: boolean | null;
    text?: string | null;
  }

  //data
  function _finalText({ iconButton, justText, text }: IfinalText) {
    if (iconButton && justText) {
      return (
        <>
          <div aria-hidden="true" className={iconWrapperClass}>
            <Img {...icon} className={iconClass} loading={load || 'lazy'} />
            <Img {...iconHover} className={iconHoverClass} loading={load || 'lazy'} />
          </div>
          <span>{text}</span>
        </>
      );
    } else if (iconButton) {
      return (
        <div aria-hidden="true" className={iconWrapperClass}>
          <Img {...icon} className={iconClass} loading={load || 'lazy'} />
          <Img {...iconHover} className={iconHoverClass} loading={load || 'lazy'} />
        </div>
      );
    } else {
      return text;
    }
  }

  const finalText = _finalText({ iconButton, justText, text });

  if (!children && !finalText && !iconButton) return null;
  const hasLinkAction = link?.action?.[0];
  const hasHref = link?.externalHref || link?.internalHref?.slug || hasLinkAction;
  if (hasHref) {
    return (
      <Link className={classes} {...{ ...props, ...link }}>
        {loading && (
          <div className={styles?.ldsRing || s.ldsRing}>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}
        {_finalChild({ finalText, loading, children })}
      </Link>
    );
  } else {
    return (
      <button className={classes} type={type} {...props} disabled={disabled || undefined}>
        {loading && (
          <div className={styles?.ldsRing || s.ldsRing}>
            <div></div>
            <div></div>
            <div></div>
            <div></div>
          </div>
        )}
        {_finalChild({ finalText, loading, children })}
      </button>
    );
  }
};

function _finalChild({
  children,
  finalText,
  loading,
}: {
  children: React.ReactNode;
  finalText?: string | React.JSX.Element | null;
  loading: boolean | null;
}) {
  return !loading && (children || finalText);
}

export default Button;
